import { useEffect, useRef, useState } from 'react';
import { CanceledError } from 'axios';
import { FIRMWARE_VERSION_SCAN_MAX_PAGES, FLEET_COMPONENTS_MAX_PAGE_SIZE } from '../../constants/hardware-inventory';
import { getApiErrorMessage } from '../../helpers/apiError';
import {
	EMPTY_VERSION_SCAN,
	collectFirmwareVersions,
	hasNextScanPage,
	mergeVersionScanPage
} from '../../helpers/firmwareVersionScan';
import { toFleetFilters } from '../../helpers/hardwareInventory';
import { getFleetComponentsPage } from '../../services/hardwareInventory';

const EMPTY_RESULT = {
	versions: [],
	isLoading: false,
	error: null,
	isPartial: false,
	scannedCount: 0,
	matchedCount: 0,
	totalItems: 0,
	unreportedCount: 0
};

/*
 * Samples the fleet for the firmware versions a component type is running.
 *
 * A local hook rather than a redux thunk because this is transient, per-modal data that nobody
 * else reads: dispatching it would either thrash a global slice ten times per selection or
 * accumulate internally and dispatch once, which is a hook wearing a thunk's clothes. It would
 * also contend with the hardwareInventory slice, which the fleet dashboard owns.
 */
export const useFleetFirmwareVersions = (componentType) => {
	const [result, setResult] = useState(EMPTY_RESULT);
	const [attempt, setAttempt] = useState(0);
	const abortRef = useRef(null);

	/*
	 * Aborting is necessary but not sufficient: a response already sitting in the microtask queue
	 * still resolves after its scan was superseded. The id says whose result is allowed to land.
	 */
	const requestIdRef = useRef(0);

	useEffect(() => {
		abortRef.current?.abort();

		if (!componentType) {
			setResult(EMPTY_RESULT);

			return undefined;
		}

		const controller = new AbortController();
		const requestId = requestIdRef.current + 1;

		abortRef.current = controller;
		requestIdRef.current = requestId;

		const isCurrent = () => {
			return requestIdRef.current === requestId;
		};

		const runScan = async () => {
			setResult({ ...EMPTY_RESULT, isLoading: true });

			let scan = EMPTY_VERSION_SCAN;
			let scannedCount = 0;
			let totalItems = 0;
			let hasMore = true;

			try {
				/*
				 * Sequential, not fanned out: page one's totalItems is what says how many pages
				 * exist, and the endpoint's sort spans three tables. The cap is a latency budget,
				 * measured in rows fetched rather than rows kept.
				 */
				for (let page = 0; page < FIRMWARE_VERSION_SCAN_MAX_PAGES && hasMore; page += 1) {
					const queryParams = {
						...toFleetFilters(componentType),
						start: scannedCount,
						limit: FLEET_COMPONENTS_MAX_PAGE_SIZE
					};

					const { data } = await getFleetComponentsPage(queryParams, { signal: controller.signal });
					const components = data?.components ?? [];

					totalItems = data?.pagination?.totalItems ?? 0;
					scan = mergeVersionScanPage(scan, components, componentType);
					scannedCount += components.length;
					hasMore = hasNextScanPage(scannedCount, components.length, FLEET_COMPONENTS_MAX_PAGE_SIZE, totalItems);
				}
			} catch (error) {
				if (error instanceof CanceledError || error?.name === 'AbortError' || !isCurrent()) {
					return;
				}

				setResult({
					...EMPTY_RESULT,
					error: getApiErrorMessage(error, 'The firmware versions seen in the fleet could not be loaded.')
				});

				return;
			}

			if (!isCurrent()) {
				return;
			}

			setResult({
				versions: collectFirmwareVersions(scan, componentType),
				isLoading: false,
				error: null,
				isPartial: hasMore,
				scannedCount: scannedCount,
				matchedCount: scan.keptCount,
				totalItems: totalItems,
				unreportedCount: scan.unreportedCount
			});
		};

		runScan();

		return () => {
			controller.abort();
		};
	}, [componentType?.id, attempt]);

	return {
		...result,
		retry: () => {
			return setAttempt((current) => {
				return current + 1;
			});
		}
	};
};

export default useFleetFirmwareVersions;
